<template>
  <div>
    <Board class="mb-6">
      <template #header class="mb-6">
        <div class="w_header">
          <BaseTitle v-text="$t('automation.title')" />
        </div>
      </template>
      <template #main>
        <BaseDivider class="my-5" :grey="true" />
        <div class="automation_section">
          <div class="action">
            <div v-if="activeFilters.length" class="filters">
              <div class="filter_icon">
                <BaseIcon icon="$mdiFilterOutline" />
              </div>
              <div
                v-for="(filter, i) in activeFilters"
                :key="`f-${i}`"
                class="filter_item"
              >
                <div class="filter_item_label">
                  {{
                    `${filter.label} : ${
                      Array.isArray(filter.labelValue)
                        ? filter.labelValues.join(", ")
                        : filter.labelValues
                    }`
                  }}
                </div>
                <div class="close_icon" @click="removeFilter(filter.key)">
                  <BaseIcon icon="$mdiClose" />
                </div>
              </div>
            </div>
            <BaseButton
              class="menu-action"
              icon="$mdiPlus"
              @click.stop="openMenu = !openMenu"
            >
              {{ $t("automation.create_btn_label") }}
              <div v-show="openMenu" class="item_contain">
                <div
                  v-for="(effect, index) in effectItems"
                  :key="index"
                  class="item-list"
                  @click.stop="openEffectModal(effect.value)"
                >
                  <BaseParagraph type="primary" v-text="effect.label" />
                </div>
              </div>
            </BaseButton>
          </div>
        </div>
        <div class="effect_list">
          <div class="filter-container">
            <div
              class="filter-title"
              @click.stop="isFilterSectionOpen = !isFilterSectionOpen"
            >
              <BaseIcon
                :icon="isFilterSectionOpen ? '$mdiMenuUp' : '$mdiMenuDown'"
                color="primary"
              />
              <span class="filter">{{ $t("utils.filters") }}</span>
              <div class="separator" />
            </div>
            <template v-if="isFilterSectionOpen">
              <div class="filter-items">
                <div
                  v-for="filter in advancedFilters"
                  :key="filter.key"
                  class="filter-item"
                >
                  <div class="filter-label-text">{{ `${filter.label} :` }}</div>
                  <template>
                    <base-autocomplete
                      v-model="filter.value"
                      :loading="filter.items.loading"
                      :items="filter.items.results"
                      :filter="() => true"
                      :search-input.sync="filter.items.queryString"
                      :item-text="filter.component.text"
                      :item-value="filter.component.value"
                      clearable
                      :placeholder="filter.label"
                      :multiple="filter.component.multiple"
                      return-object
                      class="input-field"
                      @change="
                        applyFilters(filter.key, filter.value, filter.label)
                      "
                    />
                  </template>
                </div>
              </div>
              <div class="separator" />
            </template>
          </div>
          <BaseDataTable
            :items="filteredEffect"
            :loading="effectFilter.loading"
            :loading-text="$t('utils.loadingData')"
            hide-default-footer
            :items-per-page="-1"
          >
            <template #header>
              <thead>
                <tr>
                  <th>
                    <div
                      class="d-flex align-center"
                      @click="setActiveSort('type')"
                    >
                      <div class="header_icon">
                        <BaseIcon :icon="typeSortIcon" />
                      </div>
                      {{ $tc("utils.type", 1) }}
                    </div>
                  </th>
                  <th>
                    <div
                      class="d-flex align-center"
                      @click="setActiveSort('label')"
                    >
                      <div class="header_icon">
                        <BaseIcon :icon="labelSortIcon" />
                      </div>
                      {{ $tc("utils.label") }}
                    </div>
                  </th>
                  <th>
                    <div
                      class="d-flex align-center"
                      @click="setActiveSort('trigger_once')"
                    >
                      <div class="header_icon">
                        <BaseIcon :icon="triggerSortIcon" />
                      </div>
                      {{ $t("automation.trigger_once") }}
                    </div>
                  </th>
                  <th>
                    {{ $t("utils.notify_automatically") }}
                  </th>
                  <th>
                    {{ $tc("utils.status", 2) }}
                  </th>

                  <th>
                    {{ $t("utils.trigger") }}
                  </th>
                  <th class="text-end">
                    {{ $t("utils.actions") }}
                  </th>
                </tr>
              </thead>
            </template>
            <template #item="{ item }">
              <tr class="tab_row">
                <td>
                  <div class="row_item">
                    <div class="item_label" v-text="$t(item.effectLabel)" />
                    <div
                      v-if="item.effectExtraLabel"
                      class="item_extra"
                      v-text="item.effectExtraLabel"
                    />
                    <div
                      v-if="item.xmlPattern"
                      class="item_link"
                      @click="displayXmlPattern(item.xmlPattern)"
                      v-text="$t('utils.display_xml_template')"
                    />
                  </div>
                </td>

                <td>
                  <div class="row_item">
                    <div
                      class="item_label"
                      v-text="
                        item.label ? $t(item.label) : $t('utils.noContent')
                      "
                    />
                  </div>
                </td>

                <td>
                  <div class="row_item">
                    <input
                      v-if="item.unique"
                      v-model="item.unique"
                      disabled
                      class="unique_input"
                      type="checkbox"
                    />
                    <div v-else>-</div>
                  </div>
                </td>
                <td>
                  <div class="row_item">
                    <input
                      v-if="item.notifyImmediately"
                      v-model="item.notifyImmediately"
                      disabled
                      class="unique_input"
                      type="checkbox"
                    />
                    <div v-else>-</div>
                  </div>
                </td>
                <td>
                  <div class="row_item">
                    <div class="item_label" v-text="item.statusesLabel" />
                  </div>
                </td>
                <td>
                  <div class="row_item">
                    <div class="item_label" v-text="$t(item.eventLabel)" />
                    <div class="item_extra" v-text="item.eventExtratLabel" />
                  </div>
                </td>
                <td>
                  <div class="action_section">
                    <BaseButton
                      small
                      color="primary"
                      @click="openAutomation(item)"
                    >
                      {{ $tc("utils.automate", { action_name: "" }) }}
                    </BaseButton>
                    <BaseButton
                      small
                      color="primary"
                      @click="openEditModal(item)"
                    >
                      {{ $tc("utils.edit") }}
                    </BaseButton>
                    <BaseButton
                      color="secondary"
                      class="ml-2"
                      @click="openRemoveEffectDialog(item)"
                    >
                      {{ $tc("utils.delete") }}
                    </BaseButton>
                  </div>
                </td>
              </tr>
            </template>
          </BaseDataTable>
        </div>
      </template>
    </Board>
    <AutomationRemoveDialog
      v-if="workflow && removeEffectDialogState"
      :automation-effect="selectedEffect"
      :organization-id="organizationId"
      :workflow-id="workflow.id"
      @close="removeEffectDialogState = false"
      @effectRemoved="onEffectRemoved"
    />

    <XMLPatternPreviewer
      v-if="openXmlPatternDialog"
      :xml-pattern="xmlPattern"
      @close="openXmlPatternDialog = false"
    />
    <EffectCreateAndEditDialog
      v-if="effectModalOpen"
      :organization-id="organizationId"
      :workflow="workflow"
      :selected-effect="effectSelected"
      :automation-action-to-edit="automationActionToEdit"
      @effect-edited="editEffect"
      @close="closeEffectModal"
      @effect-created="onEffectCreated"
    />
    <EffectAutomationDialog
      v-if="openEffectAutomationModal"
      :organization-id="organizationId"
      :workflow="workflow"
      :automation-action-to-edit="automationActionToEdit"
      @close="closeAutomateModal"
      @effect-edited="editEffect"
    />
    <EffectSignatureCreateAndEditDialog
      v-if="effectSignatureOpen"
      :organization-id="organizationId"
      :workflow="workflow"
      :automation-action-to-edit="automationActionToEdit"
      @effect-edited="editEffect"
      @close="closeEffectModal"
      @effect-created="onEffectCreated"
    />
  </div>
</template>

<script>
import { AutomationEffect } from "@/core/Models/AutomationEffect";
import workflowApi from "../Services/workflow.api";
import XMLPatternPreviewer from "@/modules/Admin/Components/XMLPatternPreviewer.vue";
import AutomationRemoveDialog from "@/modules/Admin/Components/AutomationRemoveDialog.vue";
import Board from "@/core/Components/Board.vue";
import { AUTOMATION_EFFECT } from "@/core/Utils/types.utils";
import EffectCreateAndEditDialog from "./EffectCreateAndEditDialog.vue";
import EffectAutomationDialog from "./EffectAutomationDialog.vue";
import EffectSignatureCreateAndEditDialog from "./EffectSignatureCreateAndEditDialog.vue";

export default {
  name: "EffectManagement",
  components: {
    Board,
    AutomationRemoveDialog,
    XMLPatternPreviewer,
    EffectCreateAndEditDialog,
    EffectAutomationDialog,
    EffectSignatureCreateAndEditDialog,
  },
  props: {
    organizationId: {
      type: [Number, String],
      required: true,
    },
    workflow: {
      type: Object,
      required: true,
    },
    automationEffects: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      isFilterSectionOpen: false,
      removeEffectDialogState: false,
      selectedEffect: null,
      automationActionToEdit: null,
      openXmlPatternDialog: false,
      openEffectAutomationModal: false,
      openMenu: false,
      advancedFilters: [
        {
          key: "type",
          label: this.$tc("utils.type", 1),
          value: [],
          multiple: false,
          component: {
            multiple: false,
            text: "label",
            value: "value",
          },
          items: {
            results: [],
            loading: false,
          },
        },

        {
          key: "status",
          label: this.$tc("utils.status", 2),
          value: [],
          component: {
            multiple: true,
            text: "name",
            value: "id",
          },
          items: {
            results: [],
            loading: false,
          },
        },
      ],
      activeFilters: [],
      effectDetails: {
        items: [],
      },
      effectFilter: {
        page: 1,
        loading: false,
      },
      effectItems: [
        {
          label: this.$t("automation.effect.set_status"),
          value: AUTOMATION_EFFECT.SET_STATUS,
        },
        {
          label: this.$t("automation.effect.send_offer"),
          value: AUTOMATION_EFFECT.SEND_OFFER,
        },
        {
          label: this.$t("automation.effect.send_signature"),
          value: AUTOMATION_EFFECT.SEND_SIGNATURE,
        },
        {
          label: this.$t("automation.effect.send_form"),
          value: AUTOMATION_EFFECT.SEND_FORM,
        },
        {
          label: this.$t("automation.effect.send_to_ged"),
          value: AUTOMATION_EFFECT.SEND_TO_GED,
          // disabled: !this.hasEnoxa,
        },
        {
          label: this.$t("automation.effect.archive_folder"),
          value: AUTOMATION_EFFECT.ARCHIVE_FOLDER,
        },

        {
          label: this.$t("automation.effect.send_campaign"),
          value: AUTOMATION_EFFECT.SEND_CAMPAIGN,
        },
        {
          label: this.$t("automation.effect.generate_doc"),
          value: AUTOMATION_EFFECT.GENERATE_DOC,
        },
        {
          label: this.$t("automation.effect.assign_collaborator"),
          value: AUTOMATION_EFFECT.ASSIGN_COLLABORATOR,
        },
      ],
      effectSelected: null,
      effectModalOpen: false,
      effectSignatureOpen: false,
      activeSort: [],
    };
  },
  computed: {
    isTypeFilterActive() {
      return this.activeFilters.some((filter) => filter.key == "type");
    },
    isStatusFilterActive() {
      return this.activeFilters.some((filter) => filter.key == "status");
    },
    typeSortIcon() {
      return this.activeSort.find((sort) => sort.key == "type")?.value == "asc"
        ? "$mdiMenuUp"
        : "$mdiMenuDown";
    },
    labelSortIcon() {
      return this.activeSort.find((sort) => sort.key == "label")?.value == "asc"
        ? "$mdiMenuUp"
        : "$mdiMenuDown";
    },
    triggerSortIcon() {
      return this.activeSort.find((sort) => sort.key == "trigger_once")
        ?.value == "asc"
        ? "$mdiMenuUp"
        : "$mdiMenuDown";
    },
    filteredEffect() {
      if (!this.effectDetails.items.length) return [];
      const sortedItems = this.activeSort.length
        ? [...this.effectDetails.items].sort((a, b) => {
            const activeSort = this.activeSort[0];
            switch (activeSort.key) {
              case "label": {
                return activeSort.value == "desc"
                  ? b.label.localeCompare(a.label)
                  : a.label.localeCompare(b.label);
              }
              case "trigger_once": {
                return activeSort.value == "asc"
                  ? b.unique - a.unique
                  : a.unique - b.unique;
              }
              case "type": {
                return activeSort.value == "desc"
                  ? b.action.localeCompare(a.action)
                  : a.action.localeCompare(b.action);
              }
              default:
                break;
            }
          })
        : [...this.effectDetails.items];
      if (!this.activeFilters.length) return sortedItems;
      const typeFilter = this.activeFilters.find(
        (filter) => filter.key == "type"
      );
      const statusFilter = this.activeFilters.find(
        (filter) => filter.key == "status"
      );

      const effectFilteredByType = sortedItems.filter((effect) => {
        // filter sur type action

        if (typeFilter && typeFilter.value == effect.action) {
          return effect;
        }
      });
      const effectFilteredByStatus = sortedItems.filter((effect) => {
        if (
          statusFilter &&
          effect.statuses.some((status) =>
            statusFilter.value.includes(status.id)
          )
        ) {
          return effect;
        }
      });
      const effectFilteredByStatusAndType = effectFilteredByType.filter(
        (effect) => {
          if (
            statusFilter &&
            effect.statuses.some((status) =>
              statusFilter.value.includes(status.id)
            )
          ) {
            return effect;
          }
        }
      );
      if (typeFilter && !statusFilter) {
        return effectFilteredByType;
      }
      if (!typeFilter && statusFilter) {
        return effectFilteredByStatus;
      }

      return effectFilteredByStatusAndType;
    },
  },
  created() {
    this.effectDetails.items = this.automationEffects.map(
      (ae) => new AutomationEffect({ ...ae })
    );
    this.initFilters();
  },
  mounted() {
    document.addEventListener("click", this.handleClickOutside);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.handleClickOutside);
  },
  methods: {
    handleClickOutside(event) {
      if (!this.$el.contains(event?.target)) {
        this.openMenu = false;
      }
    },
    setActiveSort(key) {
      const sortActive = this.activeSort.find((sort) => sort.key == key);
      if (sortActive) {
        return (sortActive.value = sortActive.value === "asc" ? "desc" : "asc");
      }
      this.activeSort = [];
      this.activeSort.push({ key, value: "asc" });
    },
    async initFilters() {
      this.advancedFilters.find(
        (filter) => filter.key === "type"
      ).items.results = [...this.effectItems];
      await this.getWorkflowDetails();
    },
    async getWorkflowDetails() {
      const statusFilter = this.advancedFilters.find(
        (filter) => filter.key === "status"
      );
      statusFilter.loading = true;
      const res = await workflowApi.getOrganizationWorkflow(
        this.$route.params.organizationId,
        this.$route.params.workflowId
      );
      statusFilter.items.results = [...res.data.statuses];
      statusFilter.loading = false;
    },
    removeFilter(key) {
      const index = this.activeFilters.findIndex((filter) => filter.key == key);
      this.activeFilters.splice(index, 1);
    },
    applyFilters(key, item, label) {
      const index = this.activeFilters.findIndex((item) => item.key == key);
      const payload = {
        key,
        label,
        value: key === "type" ? item?.value : item.map((d) => d.id),
        labelValues: key === "type" ? item?.label : item.map((d) => d.name),
      };
      if (!item || (Array.isArray(item) && !item.length)) {
        // remove activeFilterKey if no value
        return this.activeFilters.splice(index, 1);
      }
      if (index != -1) {
        return this.activeFilters.splice(index, 1, payload);
      }
      return this.activeFilters.push(payload);
    },

    openEditModal(item) {
      if (item.action === "SEND_OFFER" && item.actionConfig?.files)
        this.effectSignatureOpen = true;
      else this.effectModalOpen = true;
      this.automationActionToEdit = { ...item };
      this.effectSelected = item.action;
    },
    editEffect({ item, value }) {
      const indexToEdit = this.effectDetails.items.findIndex(
        (effect) => effect.id === item
      );
      this.effectDetails.items.splice(
        indexToEdit,
        1,
        new AutomationEffect(value)
      );
    },
    closeEffectModal() {
      if (
        this.effectSelected == AUTOMATION_EFFECT.SEND_SIGNATURE ||
        (this.automationActionToEdit?.action &&
          this.automationActionToEdit?.actionConfig)
      )
        this.effectSignatureOpen = false;
      else this.effectModalOpen = false;
      this.effectModalOpen = false;
      this.automationActionToEdit = null;
    },
    closeAutomateModal() {
      this.openEffectAutomationModal = false;
      this.automationActionToEdit = null;
    },

    async fetchWorkflowEffect() {
      this.effectFilter.loading = true;
      try {
        const res = await workflowApi.getOrganizationWorkflowEffect({
          organizationId: this.organizationId,
          workflowId: this.workflow.id,
          params: {
            page: this.effectFilter.page,
          },
        });
        this.effectDetails = {
          ...res.data,
          items: res.data.results.map((d) => new AutomationEffect(d)),
        };
        this.$emit("updatedAutomationEffects", this.effectDetails.items);
      } catch (error) {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
      this.effectFilter.loading = false;
    },
    async onEffectCreated() {
      this.effectFilter.page = 1;
      await this.fetchWorkflowEffect();
    },
    openRemoveEffectDialog(effect) {
      this.selectedEffect = effect;
      this.removeEffectDialogState = true;
    },
    onEffectRemoved(effect) {
      this.removeEffectDialogState = false;
      this.effectDetails.items = this.effectDetails.items.filter(
        (e) => e.id != effect.id
      );
      this.$emit("updatedAutomationEffects", this.effectDetails.items);
    },
    displayXmlPattern(xmlPattern) {
      this.xmlPattern = xmlPattern;
      this.openXmlPatternDialog = true;
    },
    openEffectModal(item) {
      if (item == AUTOMATION_EFFECT.SEND_SIGNATURE)
        this.effectSignatureOpen = true;
      else this.effectModalOpen = true;
      this.effectSelected = item;
      this.openMenu = !this.openMenu;
    },
    openAutomation(item) {
      this.automationActionToEdit = { ...item };
      this.openEffectAutomationModal = true;
    },
    clickOutside() {
      this.openMenu = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.w_header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 24px;
}
.inactivity_section {
  padding: 24px;
  background: #fff;
  margin-bottom: 24px;
  .header_section {
    display: flex;
    justify-content: space-between;
  }
  .alert_label {
    color: #353542;
    font-family: Inter;
    font-size: 14px;
    font-weight: 400;
    line-height: 17px;
    display: flex;
    gap: 4px;
    align-items: center;

    .days {
      font-weight: bold;
      color: #ff5267;
      line-height: 14px;
      padding: 4px 6px;
      border-radius: 4px;
      background: rgba(#ff5267, 0.2);
    }
  }
}
.automation_section {
  .action {
    display: flex;
    justify-content: flex-start;
    .menu-action {
      position: relative;
      margin-left: auto;
      .item_contain {
        position: absolute;
        z-index: 100;
        right: -50px;
        top: 30px;
        border-radius: 5px;
        width: 200px;
        background: #fff;
        box-shadow: 0px 6px 10px 0px rgba(46, 45, 193, 0.06),
          0px 2px 4px 0px rgba(46, 45, 193, 0.1);
        .item-list {
          padding: 12px;
          height: 45px;
        }
      }
    }
  }
}
.effect_list {
  padding-top: 16px;
  .tab_row {
    .row_item {
      padding: 16px 0;
      font-family: "Inter";
      font-style: normal;
      .item_label {
        color: #353542;
        font-size: 12px;
        font-weight: 600;
        line-height: 20px;
      }
      .item_extra {
        color: #707080;
        font-size: 12px;
        font-weight: 400;
        line-height: 16px;
        text-transform: uppercase;
      }
      .item_link {
        color: #2536cc;
        font-size: 12px;
        font-weight: 400;
        line-height: 16px;
        text-decoration: underline;
        cursor: pointer;
      }
      .unique_input {
        width: 18px;
        height: 18px;
      }
    }
    .action_section {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: 4px;
    }
  }
}

// new
.item-list {
  display: flex;
  align-items: center;
  height: 33px;
  cursor: pointer;
  &:hover {
    background: #f4f7ff;
  }
}
.filter-container {
  display: block;
  margin: 16px 0;
  .filter-title {
    display: flex;
    align-items: center;
    color: #2536cc;
    font-size: 16px;
    cursor: pointer;
  }
  .filter-items {
    margin: 24px 0 24px 24px;
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
    ::v-deep {
      .v-text-field__details {
        display: none;
      }
    }

    .filter-item {
      display: flex;
      gap: 8px;
      height: 40px;
      align-items: center;
      width: 48%;
      .filter-label-text {
        width: clamp(100px, 120px, 300px);
      }
      .input-field {
        max-width: 320px;
      }
    }
  }
}
.separator {
  width: 100%;
  border: 1px solid #2536cc;
}
.filters {
  display: flex;
  padding: 0 24px 0 0;
  align-items: center;
  .filter_icon {
    flex: none;
    background: #f4f7ff;
    border-radius: 9px;
    height: 36px;
    width: 36px;
    display: flex;
    justify-content: center;
    align-items: center;
    &::v-deep {
      .v-icon {
        height: 20px;
        width: 20px;
        color: #242533;
      }
    }
  }

  .filter_item {
    border-radius: 36px;
    background: #f4f7ff;
    padding: 8px 16px;
    display: flex;
    align-items: center;
    margin-left: 8px;
    .filter_item_icon {
      width: 18px;
      height: 18px;
      margin-right: 4px;
      display: flex;
      align-items: center;
      justify-content: center;
      &::v-deep {
        .v-icon {
          height: 18px;
          width: 18px;
          color: #707080;
        }
      }
    }
    .filter_item_label {
      color: #242533;
      font-family: "Inter";
      font-size: 12px;
      line-height: 20px;
      font-style: normal;
      font-weight: 500;
    }
    .close_icon {
      width: 18px;
      height: 18px;
      margin-left: 4px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      &::v-deep {
        .v-icon {
          height: 18px;
          width: 18px;
          color: #2536cc;
        }
      }
    }
  }
}
.header_icon {
  background: #d0dffd;
  flex: none;
  width: 16px;
  height: 16px;
  border-radius: 16px;
  overflow: hidden;
  margin-right: 8px;
  display: flex;
  justify-content: center;
  align-items: center;

  &::v-deep {
    .v-icon {
      height: 18px;
      width: 18px;
    }
  }
}
</style>
